const FUNNEL_CONCEPT_QUERY_PARAM = "concept";

export const FUNNEL_CONCEPTS = {
  agents: {
    key: "agents-angle",
    label: "Agents angle",
    queryValues: ["agents", "agent", "agents-angle", "default", "mission-control", "fleet", "notch", "board"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "All your agents.",
    heroTitleAccent: "One notch.",
    heroSubtitle: "Claude Code, Codex, Cursor and your own agents, all on one notch. See who's working, who's done, and who needs you.",
  },
  // The closed-lid angle: the one ad concept still in use alongside the default.
  halfOpen: {
    key: "half-open-angle",
    label: "Half-open angle",
    queryValues: ["keepawake", "keep-awake", "lid-close", "lid-closed", "closed-lid", "half-open", "half-open-walk", "half-open-angle", "halfopen", "lid"],
    heroTitleLayout: "stacked" as const,
    heroTitleMain: "Lid closed.",
    heroTitleAccent: "Agents awake.",
    heroSubtitle: "Keep your agents awake while your Mac is closed or locked, so Claude Code and Codex keep working without you.",
    deepMatch: {
      thread: "Keep your agents awake.",
      // The quiz must sell staying awake, not the notch, or the ad's promise drops here.
      quizSubtitles: {
        agents_used: "Pick all that apply. AgentMax keeps every one of them awake while your Mac is closed or locked.",
        agent_hours: "Count all of it, especially the runs you walk away from.",
        mac_setup: "Last one. We'll match you to the right license.",
      },
      // Split on "agents": the visitor's picked agents are injected in their place.
      resultMetricLabel: "of agents that stop the moment you close or lock your Mac.",
      resultProblems: ["runs stopping when you close or lock your Mac", "sitting there just to keep your agents alive"],
      // paymentExplainerSteps[3] is the close-lid continuity beat.
      featuredExplainerIndex: 3,
      productImage: {
        src: "/assets/product/keep-mac-awake.jpg",
        alt: "AgentMax's Keep Mac Awake panel: the toggle on and battery-aware, with allow display sleep, allow closed lid, and a turn-off timer.",
        width: 1280,
        height: 1024,
      },
      paymentTitle: "Lid closed. Agents awake.",
      // Offer-neutral: this concept runs on the trial today but must not claim either path.
      paymentBody: "Keep your agents awake while your Mac is closed or locked. Claude Code and Codex keep working, with battery guardrails on.",
      // Required by the DeepMatch type but not rendered anywhere today.
      resultHeadline: "Keep your agents awake, lid closed or locked.",
      resultKeyFeature: {
        title: "Agents stay awake, lid closed or locked",
        copy: "Close or lock your Mac and Claude Code, Codex, and Cursor keep working. Battery guardrails stay on.",
        icon: "moon" as const,
      },
    },
  },
} as const;

export type FunnelConceptKey = (typeof FUNNEL_CONCEPTS)[keyof typeof FUNNEL_CONCEPTS]["key"];
export type FunnelConcept = (typeof FUNNEL_CONCEPTS)[keyof typeof FUNNEL_CONCEPTS];

// Optional deep-match payload carried by the ads-v2 concepts. Threads the ad's promise
// past the hero: swaps the quiz demand question, leads the result, and features the
// matching payment beat. Concepts without it fall through to today's shared behavior.
export type DeepMatch = {
  thread: string;
  /** Which concept-specific hero visual to show on the welcome step. */
  heroVisual?: "board" | "jump" | "cost" | "voice" | "loop";
  quizSwapQuestionId?: "voice_demand" | "loop_demand";
  /** Per-question subtitle overrides (keyed by question id) so the quiz carries this angle, not the default one. */
  quizSubtitles?: Readonly<Record<string, string>>;
  /** Replaces the recorded demo on the result + checkout steps with a still that sells this angle. */
  productImage?: Readonly<{ src: string; alt: string; width: number; height: number }>;
  resultHeadline: string;
  /** Concept-framed caption under the result hero metric (matches the ad angle, not generic visibility). */
  resultMetricLabel: string;
  /** Concept-framed "Your setup today" pains (first line is highlighted). */
  resultProblems: [string, string];
  resultKeyFeature: {
    title: string;
    copy: string;
    icon: "moon" | "battery" | "terminal" | "clock" | "shield" | "desktop";
  };
  featuredExplainerIndex: number;
  /** Cloud-based concept (loop): suppress local-first / on-your-Mac claims that would contradict it. */
  cloudConcept?: boolean;
  /** Checkout hero, reframed to the ad angle (the default one-time offer path). */
  paymentTitle: string;
  paymentBody: string;
  /** Optional lead card in the checkout's "How AgentMax works" grid (voice/loop feature-led concepts). */
  paymentFeatureCard?: { title: string; description: string };
};

/** Reads the optional deep-match block off any concept (only the ads-v2 concepts carry one). */
export const getDeepMatch = (concept: FunnelConcept): DeepMatch | undefined =>
  (concept as { deepMatch?: DeepMatch }).deepMatch;

const conceptEntries = Object.values(FUNNEL_CONCEPTS);
const conceptByQueryValue = new Map(
  conceptEntries.flatMap((concept) => concept.queryValues.map((value) => [value.toLowerCase(), concept] as const)),
);

export const DEFAULT_FUNNEL_CONCEPT = FUNNEL_CONCEPTS.agents;

export const isFunnelConceptKey = (value: string): value is FunnelConceptKey =>
  conceptEntries.some((concept) => concept.key === value);

export const getFunnelConceptByKey = (key: FunnelConceptKey): FunnelConcept =>
  conceptEntries.find((concept) => concept.key === key) ?? DEFAULT_FUNNEL_CONCEPT;

export const getFunnelConceptFromLocation = (locationLike: Pick<Location, "href"> | URL): FunnelConcept | null => {
  const url = locationLike instanceof URL ? locationLike : new URL(locationLike.href);
  const raw = url.searchParams.get(FUNNEL_CONCEPT_QUERY_PARAM)?.trim().toLowerCase();
  if (!raw) return null;
  return conceptByQueryValue.get(raw) ?? null;
};

export const getStoredOrDefaultFunnelConcept = (storedConceptKey: string | null | undefined): FunnelConcept =>
  storedConceptKey && isFunnelConceptKey(storedConceptKey)
    ? getFunnelConceptByKey(storedConceptKey)
    : DEFAULT_FUNNEL_CONCEPT;
